import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSourcesBySourceNamespaceUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/get-sources-by-source-namespace-use-case";
import { createMachineGroupUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/create-machine-group-use-case";
import { updateMachineGroupUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/update-machine-group-use-case";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { updateShowAddMachineGroupsAdminPanelModal } from "@src/reducers/your-reducer";
import {
  CrossNameSpaceOrganziation,
  MachineGroup,
} from "@src/types/namespace-configuration";
import checkBoxIcon from "@src/assets/svg/checkbox-icon.svg";
import checkboxIndeterminateIcon from "@src/assets/svg/checkbox-indeterminate.svg";
import checkedCheckboxIcon from "@src/assets/svg/checked-checkbox-icon.svg";

interface MachineGroupFormValues {
  name: string;
  organization_id: string;
  source_ids: string[];
}

interface MachineRow {
  sourceId: string;
  name: string;
  manufacturer: string;
  hostname: string;
}

export const useAddMachineGroupsAdminPanelModalHook = (
  editingMachineGroup: MachineGroup | undefined,
  onMachineGroupSaved: (createdMachineGroupId?: string) => void,
  organizations: CrossNameSpaceOrganziation[],
  isLoadingOrganizations: boolean,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const [machines, setMachines] = useState<MachineRow[]>([]);
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formValues, setFormValues] = useState<MachineGroupFormValues>(() => ({
    name: editingMachineGroup?.name ?? "",
    organization_id: editingMachineGroup?.organization_id ?? "",
    source_ids:
      editingMachineGroup?.sources.map((source) => source.source_id) ?? [],
  }));
  const closeModal = useCallback(() => {
    dispatch(updateShowAddMachineGroupsAdminPanelModal(false));
  }, [dispatch]);

  const texts = {
    title: i18n(
      editingMachineGroup
        ? "modules.addMachineGroupsAdminPanelModal.editTitle"
        : "modules.addMachineGroupsAdminPanelModal.title",
    ),
    groupNameLabel: i18n(
      "modules.addMachineGroupsAdminPanelModal.groupNameLabel",
    ),
    groupNamePlaceholder: i18n(
      "modules.addMachineGroupsAdminPanelModal.groupNamePlaceholder",
    ),
    organizationLabel: i18n(
      "modules.addMachineGroupsAdminPanelModal.organizationLabel",
    ),
    fixValidateFieldText: i18n(
      "modules.addMachineGroupsAdminPanelModal.fixValidateFieldText",
    ),
    selectOrganization: i18n(
      "modules.addMachineGroupsAdminPanelModal.selectOrganization",
    ),
    machineLibraryLabel: i18n(
      "modules.addMachineGroupsAdminPanelModal.machineLibraryLabel",
    ),
    chooseOrganizationToSeeMachineLibrary: i18n(
      "modules.addMachineGroupsAdminPanelModal.chooseOrganizationToSeeMachineLibrary",
    ),
    noMachinesFound: i18n(
      "modules.addMachineGroupsAdminPanelModal.noMachinesFound",
    ),
    machineName: i18n("modules.addMachineGroupsAdminPanelModal.machineName"),
    manufacturer: i18n("modules.addMachineGroupsAdminPanelModal.manufacturer"),
    ipAddress: i18n("modules.addMachineGroupsAdminPanelModal.ipAddress"),
    saveButton: i18n("modules.addMachineGroupsAdminPanelModal.saveButton"),
    cancelButton: i18n("modules.addMachineGroupsAdminPanelModal.cancelButton"),
    errorFetchingMachines: i18n(
      "modules.addMachineGroupsAdminPanelModal.errorFetchingMachines",
    ),
    errorCreatingMachineGroup: i18n(
      "modules.addMachineGroupsAdminPanelModal.errorCreatingMachineGroup",
    ),
    errorUpdatingMachineGroup: i18n(
      "modules.addMachineGroupsAdminPanelModal.errorUpdatingMachineGroup",
    ),
    machineGroupCreatedSuccessfully: i18n(
      "modules.addMachineGroupsAdminPanelModal.machineGroupCreatedSuccessfully",
    ),
    machineGroupUpdatedSuccessfully: i18n(
      "modules.addMachineGroupsAdminPanelModal.machineGroupUpdatedSuccessfully",
    ),
  };

  useEffect(() => {
    let cancelled = false;
    const selectedOrganization = organizations.find(
      (organization) => organization.id === formValues.organization_id,
    );

    setMachines([]);
    if (isLoadingOrganizations && formValues.organization_id) {
      setIsLoadingMachines(true);
      return () => {
        cancelled = true;
      };
    }

    if (!authContext || !selectedOrganization) {
      setIsLoadingMachines(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoadingMachines(true);

    const loadMachines = async () => {
      try {
        const result = await getSourcesBySourceNamespaceUseCase(
          selectedOrganization.namespace_id,
        );
        if (cancelled) return;

        if (!result.success || !result.data) {
          errorSnackbar(texts.errorFetchingMachines);
          return;
        }

        setMachines(
          Object.values(result.data).map((source) => ({
            sourceId: source.source_id,
            name: source.name,
            manufacturer: source.build_info?.manufacturer || "",
            hostname: source.hostname || "",
          })),
        );
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          errorSnackbar(texts.errorFetchingMachines);
        }
      } finally {
        if (!cancelled) setIsLoadingMachines(false);
      }
    };

    void loadMachines();

    return () => {
      cancelled = true;
    };
  }, [
    authContext,
    formValues.organization_id,
    isLoadingOrganizations,
    organizations,
  ]);

  const organizationOptions: Record<string, string> = {};
  organizations.forEach((organization) => {
    organizationOptions[organization.id] = organization.name;
  });
  const organizationPlaceholder = texts.selectOrganization;

  const handleGroupNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((current) => ({ ...current, name: event.target.value }));
  };

  const handleClearGroupName = () => {
    setFormValues((current) => ({ ...current, name: "" }));
  };

  const handleOrganizationChange = (organizationId: string) => {
    setFormValues((current) => ({
      ...current,
      organization_id: organizationId,
      source_ids: [],
    }));
  };

  const toggleMachine = (sourceId: string) => {
    setFormValues((current) => ({
      ...current,
      source_ids: current.source_ids.includes(sourceId)
        ? current.source_ids.filter((id) => id !== sourceId)
        : [...current.source_ids, sourceId],
    }));
  };

  const toggleAllMachines = () => {
    setFormValues((current) => {
      const machineIds = machines.map((machine) => machine.sourceId);
      const allMachinesSelected =
        machineIds.length > 0 &&
        machineIds.every((id) => current.source_ids.includes(id));

      return {
        ...current,
        source_ids: allMachinesSelected
          ? current.source_ids.filter((id) => !machineIds.includes(id))
          : Array.from(new Set([...current.source_ids, ...machineIds])),
      };
    });
  };

  const handleSave = async () => {
    if (isSaving) return;

    const missingFields = [];
    if (!formValues.name.trim()) missingFields.push(texts.groupNameLabel);
    if (!formValues.organization_id) {
      missingFields.push(texts.organizationLabel);
    }

    if (missingFields.length > 0) {
      warningSnackbar(
        `${texts.fixValidateFieldText} ${missingFields.join(", ")}.`,
        5000,
      );
      return;
    }

    setIsSaving(true);
    const payload = {
      name: formValues.name.trim(),
      organization_id: formValues.organization_id,
    };
    const result = editingMachineGroup
      ? await updateMachineGroupUseCase(editingMachineGroup.id, {
          ...payload,
          description: editingMachineGroup.description,
          source_ids: formValues.source_ids,
        })
      : await createMachineGroupUseCase({
          ...payload,
          ...(formValues.source_ids.length > 0 && {
            source_ids: formValues.source_ids,
          }),
        });
    setIsSaving(false);

    if (!result.success) {
      errorSnackbar(
        result.error ||
          (editingMachineGroup
            ? texts.errorUpdatingMachineGroup
            : texts.errorCreatingMachineGroup),
      );
      return;
    }

    successSnackbar(
      editingMachineGroup
        ? texts.machineGroupUpdatedSuccessfully
        : texts.machineGroupCreatedSuccessfully,
    );
    onMachineGroupSaved(editingMachineGroup ? undefined : result.data?.id);
    closeModal();
  };

  return {
    closeModal,
    formValues,
    handleGroupNameChange,
    handleClearGroupName,
    handleOrganizationChange,
    handleSave,
    isLoadingMachines,
    isSaving,
    machines,
    organizationOptions,
    organizationPlaceholder,
    texts,
    toggleMachine,
    toggleAllMachines,
    checkBoxIcon,
    checkboxIndeterminateIcon,
    checkedCheckboxIcon,
  };
};
